import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminAccount } from '../../../api/types';
import { useAdminAuth } from '../../../app/providers/useAdminAuth';
import { Badge } from '../../../shared/ui/Badge';
import { Button } from '../../../shared/ui/Button';
import { Table } from '../../../shared/ui/Table';
import { useDeactivateAdmin } from '../hooks/useDeactivateAdmin';
import { useMfaReset } from '../hooks/useMfaReset';
import { useReactivateAdmin } from '../hooks/useReactivateAdmin';
import { DisableMfaModal } from './DisableMfaModal';
import { EnableMfaModal } from './EnableMfaModal';
import styles from './AdminAccountsTable.module.css';

interface AdminAccountsTableProps {
  admins: AdminAccount[];
}

// PARA-114, AC-1/AC-3/AC-4: таблица admin-аккаунтов + действия
// "Deactivate"/"Reactivate" (по паттерну UsersTable, PARA-106).
// PARA-151, AC-1/AC-6/AC-7: MFA-действия — в своей строке (adminId из
// auth-контекста) "Enable/Disable MFA" (модалки), в чужих строках с
// включённой MFA — "Reset MFA" с подтверждением (без TOTP-кода).
export function AdminAccountsTable({ admins }: AdminAccountsTableProps): JSX.Element {
  const { t } = useTranslation();
  const { adminId } = useAdminAuth();
  const deactivate = useDeactivateAdmin();
  const reactivate = useReactivateAdmin();
  const mfaReset = useMfaReset();
  const [enableMfaOpen, setEnableMfaOpen] = useState(false);
  const [disableMfaOpen, setDisableMfaOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminAccount | null>(null);

  const submitReset = (): void => {
    if (resetTarget === null) {
      return;
    }
    mfaReset.mutate(resetTarget.id, { onSuccess: () => setResetTarget(null) });
  };

  return (
    <div>
      <Table>
        <thead>
          <tr>
            <th>{t('admins.id')}</th>
            <th>{t('admins.login')}</th>
            <th>{t('admins.status')}</th>
            <th>{t('admins.createdAt')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {admins.map((admin) => (
            <tr key={admin.id}>
              <td className={styles.num}>{admin.id}</td>
              <td>{admin.login}</td>
              <td>
                {admin.is_active ? (
                  <Badge tone="positive">{t('admins.active')}</Badge>
                ) : (
                  <Badge tone="muted">{t('admins.deactivated')}</Badge>
                )}
              </td>
              <td className={styles.num}>{admin.created_at ?? '—'}</td>
              <td className={styles.actions}>
                {admin.is_active ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={deactivate.isPending}
                    onClick={() => deactivate.mutate(admin.id)}
                  >
                    {t('admins.deactivate')}
                  </Button>
                ) : (
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={reactivate.isPending}
                    onClick={() => reactivate.mutate(admin.id)}
                  >
                    {t('admins.reactivate')}
                  </Button>
                )}
                {admin.id === adminId ? (
                  admin.mfa_enabled ? (
                    <Button
                      variant="soft"
                      size="sm"
                      className={styles.actionButton}
                      onClick={() => setDisableMfaOpen(true)}
                    >
                      {t('admins.mfa.disable')}
                    </Button>
                  ) : (
                    <Button
                      variant="soft"
                      size="sm"
                      className={styles.actionButton}
                      onClick={() => setEnableMfaOpen(true)}
                    >
                      {t('admins.mfa.enable')}
                    </Button>
                  )
                ) : (
                  admin.mfa_enabled && (
                    <Button
                      variant="soft"
                      size="sm"
                      className={styles.actionButton}
                      disabled={mfaReset.isPending}
                      onClick={() => setResetTarget(admin)}
                    >
                      {t('admins.mfa.reset')}
                    </Button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(deactivate.isError || reactivate.isError || mfaReset.isError) && (
        <p className={styles.error} role="alert">
          {t('admins.createError')}
        </p>
      )}

      {enableMfaOpen && <EnableMfaModal onClose={() => setEnableMfaOpen(false)} />}
      {disableMfaOpen && <DisableMfaModal onClose={() => setDisableMfaOpen(false)} />}

      {resetTarget !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-mfa-title"
          className={styles.dialogOverlay}
        >
          <div className={styles.dialogContent}>
            <h3 id="reset-mfa-title">
              {t('admins.mfa.resetConfirmTitle', { login: resetTarget.login })}
            </h3>
            <p className={styles.dialogField}>{t('admins.mfa.resetConfirmDescription')}</p>
            <div className={styles.dialogActions}>
              <Button
                variant="ghost"
                onClick={() => setResetTarget(null)}
                disabled={mfaReset.isPending}
                className={styles.dialogActionButton}
              >
                {t('admins.mfa.cancel')}
              </Button>
              <Button variant="primary" onClick={submitReset} disabled={mfaReset.isPending}>
                {t('admins.mfa.resetConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
